import { useTranslation } from "react-i18next";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import type { BaseButtonStyle } from "components/designV2/buttons/baseButton";
import { Tooltip } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { toRgba } from "config/colors.rgba.logic";
import type { EditingTab } from "../profilePanel.contract";
import {
  Footer,
  LockedEditButton,
  LockedEditTooltip,
  LockedEditWrap,
  Message,
} from "./profilePanel.styled";

interface ProfilePanelFooterProps {
  readonly canEdit: boolean;
  readonly canSaveEmploymentEdit: boolean;
  readonly canSavePersonalEdit: boolean;
  readonly canSaveProfileEdit: boolean;
  readonly editingTab: EditingTab;
  readonly isDeleting: boolean;
  readonly isEditLocked: boolean;
  readonly isSaving: boolean;
  readonly saveError: string;
  readonly showAddEmploymentButton: boolean;
  readonly showDeleteButton: boolean;
  readonly showEditButton: boolean;
  readonly showEndEmploymentButton: boolean;
  readonly showLockedEditHint: boolean;
  readonly onAddEmploymentClick: () => void;
  readonly onCancelEdit: () => void;
  readonly onDeleteClick: () => void;
  readonly onEditClick: () => void;
  readonly onEndEmploymentClick: () => void;
  readonly onOpenSaveModal: () => void;
  readonly onSetLockedEditHint: (value: boolean) => void;
}

const DANGER_SECONDARY_BUTTON_STYLE_OVERRIDES: Partial<BaseButtonStyle> = {
  textColor: ColorV2.NegativeRed,
  borderColor: ColorV2.NegativeRed,
  iconColor: ColorV2.NegativeRed,
  hoverBackgroundColor: ColorV2.NegativeRedLight,
  activeBackgroundColor: toRgba(ColorV2.NegativeRed, 0.2),
};

export const ProfilePanelFooter = (
  props: ProfilePanelFooterProps,
): JSX.Element => {
  const { t } = useTranslation();

  const deleteButton = props.showDeleteButton ? (
    <SecondaryButton
      label={t("Staff.ProfilePanelDelete")}
      iconName="v2/trash"
      onClick={props.onDeleteClick}
      disabled={props.isDeleting}
      styleOverrides={DANGER_SECONDARY_BUTTON_STYLE_OVERRIDES}
    />
  ) : null;

  return (
    <Footer>
      {props.saveError.length > 0 && <Message>{props.saveError}</Message>}
      {!props.canEdit ? null : props.isEditLocked ? (
        <>
          {deleteButton}
          {props.showEditButton && (
            <LockedEditWrap
              onMouseEnter={() => props.onSetLockedEditHint(true)}
              onMouseLeave={() => props.onSetLockedEditHint(false)}
            >
              {props.showLockedEditHint && (
                <LockedEditTooltip>
                  <Tooltip
                    text={t("Staff.ProfilePanelEditLockedInfo")}
                    arrow="down"
                  />
                </LockedEditTooltip>
              )}
              <LockedEditButton
                label={t("Staff.ProfilePanelEdit")}
                iconName="lock"
                size="large"
                disabled
              />
            </LockedEditWrap>
          )}
        </>
      ) : (
        <>
          {props.editingTab != null ? (
            <>
              <SecondaryButton
                label={t("General.Cancel")}
                onClick={props.onCancelEdit}
                disabled={props.isSaving}
              />
              {(props.editingTab === "employmentSalary" ||
                (props.editingTab === "profile" &&
                  props.canSaveProfileEdit)) && (
                <PrimaryButton
                  label={t("General.Save")}
                  onClick={props.onOpenSaveModal}
                  disabled={
                    props.isSaving ||
                    (props.editingTab === "employmentSalary" &&
                      !props.canSaveEmploymentEdit) ||
                    (props.editingTab === "profile" &&
                      !props.canSavePersonalEdit)
                  }
                />
              )}
            </>
          ) : (
            <>
              {props.showAddEmploymentButton && (
                <SecondaryButton
                  label={t("Staff.ProfilePanelNewAgreement")}
                  iconName="v2/plus"
                  onClick={props.onAddEmploymentClick}
                  disabled={props.isDeleting}
                />
              )}
              {props.showEndEmploymentButton && (
                <SecondaryButton
                  label={t("Staff.ProfilePanelEndEmploymentButton")}
                  iconName="v2/cross-person"
                  onClick={props.onEndEmploymentClick}
                  disabled={props.isDeleting}
                  styleOverrides={DANGER_SECONDARY_BUTTON_STYLE_OVERRIDES}
                />
              )}
              {deleteButton}
              {props.showEditButton && (
                <SecondaryButton
                  label={t("Staff.ProfilePanelEdit")}
                  iconName="v2/pen"
                  onClick={props.onEditClick}
                  disabled={props.isDeleting}
                />
              )}
            </>
          )}
        </>
      )}
    </Footer>
  );
};
